<template>
  <div class="pescaria-acao">
    <!-- ============ A COLUNA DO PESCADOR: o que você leva para a água ============ -->
    <aside class="pescaria-acao__coluna">
      <!-- Os quatro números da pescaria, com os ícones da roda (D19/D20) — sem emoji (UI-10).
           É aqui que o D28 vai escrever o poder dividido quando houver tempestade. -->
      <div class="pescaria-acao__bilhete pescaria-acao__atributos">
        <span v-for="atributo in atributos" :key="atributo.icone" class="pescaria-acao__atributo">
          <FishIcon :nome="atributo.icone" tamanho="pequeno" />
          <strong class="t-numero">{{ atributo.valor }}</strong>
        </span>
      </div>

      <!-- A vara na mão, com o medidor de verdade — a durabilidade É um medidor (2.8). -->
      <div v-if="hasRod" class="pescaria-acao__bilhete pescaria-acao__vara">
        <FishIcon :src="getItemImage(getEquippedRod.name)" :alt="getEquippedRod.name" tamanho="grande" />
        <div class="crescer">
          <p class="pescaria-acao__nome">{{ getEquippedRod.name }}</p>
          <FishMeter
            :valor="getEquippedRod.durability?.current ?? 0"
            :maximo="getEquippedRod.durability?.maxDurability || 1"
            :cor="corDaDurabilidade"
            tamanho="fino"
            :rotulo-acessivel="`Durabilidade: ${rodDurabilityPercent}%`"
          />
        </div>
      </div>

      <!-- A lata de iscas: encaixes, não cards brancos. Tocar escolhe; o aro diz qual está na linha. -->
      <div v-if="playerBaits.length" class="pescaria-acao__bilhete">
        <p class="pescaria-acao__rotulo">{{ $t('fishing.selectBait') }}</p>
        <div class="pescaria-acao__iscas">
          <FishSlot
            v-for="bait in playerBaits"
            :key="bait.id"
            :item="{ id: bait.id, nome: getTranslatedBaitName(bait.name), imagem: getItemImage(bait.name) }"
            :quantidade="bait.quantity"
            :selecionado="selectedBaitId === bait.id"
            @selecionar="selectBait(bait)"
          />
        </div>
        <p v-if="iscaSelecionada" class="pescaria-acao__isca-nota">
          {{ getTranslatedBaitName(iscaSelecionada.name) }} · +{{ iscaSelecionada.fishingPower }} {{ $t('fishing.power') }}
        </p>
      </div>

      <!-- As poções do pescador: o que está valendo e o que dá para beber agora. -->
      <PocoesDePesca />

      <!-- Faltando apetrecho, o bilhete diz o que falta E leva à Vila (D11). -->
      <div v-if="!playerBaits.length" class="pescaria-acao__bilhete pescaria-acao__aviso">
        <p>{{ $t('fishing.noBait') }}</p>
        <FishButton tamanho="pequeno" icone="nav-loja" @click="goToShop">{{ $t('fishing.goToShop') }}</FishButton>
      </div>

      <div v-if="!hasRod" class="pescaria-acao__bilhete pescaria-acao__aviso">
        <p>{{ $t('fishing.needRod') }}</p>
        <FishButton tamanho="pequeno" icone="nav-loja" @click="goToShop">{{ $t('fishing.buyRod') }}</FishButton>
      </div>

      <!-- Sem vara nenhuma no baú: a pesca com as mãos, com as regras no bilhete. -->
      <div v-if="showHandFishing" class="pescaria-acao__bilhete">
        <p class="pescaria-acao__rotulo">{{ $t('fishing.buttons.handFishing') }}</p>
        <p class="pescaria-acao__miudo">{{ $t('fishing.handFishing.info') }}</p>
        <ul class="pescaria-acao__regras">
          <li>{{ $t('fishing.handFishing.rule1') }}</li>
          <li>{{ $t('fishing.handFishing.rule2') }}</li>
          <li>{{ $t('fishing.handFishing.rule3') }}</li>
        </ul>
        <FishButton bloco :desabilitado="isFishing" som="clique" @click="startHandFishing">
          {{ $t('fishing.buttons.handFishing') }}
        </FishButton>
      </div>
    </aside>

    <!-- ============ O PALCO: o pescador no barco, o peixe, a falha ============ -->
    <!-- O bonequinho no barco FICA (o autor gosta dele, 03/09: "tinha uma animação bonitinha") —
         é a exceção registrada à regra do pack, como o medidor e as fitas. A espera é ele pescando
         com a fala embaixo, não uma barra de loading. -->
    <div class="pescaria-acao__palco">
      <div v-if="isFishing" class="pescaria-acao__espera">
        <FishingCharacter :is-pulling="isPulling" />
        <p class="pescaria-acao__bilhete pescaria-acao__estado">{{ fishingState }}</p>
      </div>

      <Transition name="fade">
        <div v-if="fishingResult && !isFishing" class="pescaria-acao__bilhete pescaria-acao__falha">
          <p>{{ $t(fishingResult.message) }}</p>
          <FishButton v-if="fishingResult.status === 'NO_STAMINA'" tamanho="pequeno" icone="home" @click="$router.push('/house-rest')">
            {{ $t('fishing.buttons.cantFish') }}
          </FishButton>
        </div>
      </Transition>
    </div>

    <CatchResult v-if="caughtFish && !isFishing" :fish="caughtFish" @close="resetCatch" />

    <!-- ============ A AÇÃO PRINCIPAL, na base, sempre visível — com o motivo (D11) ============ -->
    <div class="acao-principal pescaria-acao__lancar">
      <FishButton
        v-if="!showHandFishing"
        variante="primario"
        bloco
        :desabilitado="!canStartFishing"
        :motivo="motivoDoLancamento"
        :carregando="isFishing"
        som="clique"
        @click="startFishing"
      >
        {{ textoDoLancamento }}
      </FishButton>
    </div>

    <GameAlert
      v-if="showAlert"
      :show="showAlert"
      :message="alertMessage"
      type="warning"
      @close="showAlert = false"
    />

    <FirstFishCelebration
      v-if="showFirstFishCelebration"
      :show="showFirstFishCelebration"
      :fish="caughtFish"
      :bonus="firstFishBonus"
      :playerId="$store.state.player.id"
      @close="hideFirstFishCelebration"
    />
  </div>
</template>

<script>
import FishingCharacter from './FishingCharacter.vue';
import CatchResult from './CatchResult.vue';
import GameAlert from '@/shared/common/Alert.vue';
import FirstFishCelebration from './FirstFishCelebration.vue';
import PocoesDePesca from '@/shared/pescador/PocoesDePesca.vue';
import { mapState, mapGetters, mapActions } from 'vuex';
import { getItemImage } from '@/shared/utils/itemImages';
import { baitTranslations } from '@/i18n/locales/baits';
import { FishIcon, FishButton, FishSlot, FishMeter } from '@/shared/ui';

export default {
  name: 'FishingAction',
  components: {
    FishingCharacter,
    CatchResult,
    GameAlert,
    FirstFishCelebration,
    PocoesDePesca,
    FishIcon,
    FishButton,
    FishSlot,
    FishMeter
  },
  data() {
    return {
      selectedBaitId: null,
      isFishing: false,
      fishingState: '',
      loadingProgress: 0,
      isPulling: false,
      caughtFish: null,
      fishingResult: null,
      showAlert: false,
      alertMessage: '',
      isResting: false,
      currentAmbientSound: null,
      autoEquipInProgress: false,
      isHandFishing: false,
      showFirstFishCelebration: false,
      firstFishBonus: 0,
    };
  },
  computed: {
    ...mapState('player', [
      'inventory',
      'equipment',
      'stats',
      'stamina',
      'currentMap',
      'firstFishOfDay'
    ]),
    ...mapGetters('player', ['canFish', 'getEquippedRod']),
    playerBaits() {
      return this.inventory.baits.filter(bait => bait.quantity > 0);
    },
    iscaSelecionada() {
      return this.playerBaits.find(b => b.id === this.selectedBaitId) || null;
    },
    atributos() {
      return [
        { icone: 'atributo-poder', valor: `${(this.stats.fishingPower || 0).toFixed(0)}` },
        { icone: 'atributo-captura', valor: `${((this.stats.catchRate || 0) * 100).toFixed(0)}%` },
        { icone: 'atributo-critico', valor: `${((this.stats.criticalRate || 0) * 100).toFixed(0)}%` },
        { icone: 'atributo-recolhimento', valor: `${(this.stats.reelSpeed || 1).toFixed(1)}x` },
      ];
    },
    textoDoLancamento() {
      if (this.isFishing) return this.$t('fishing.buttons.fishing');
      return this.$t('fishing.buttons.cast');
    },
    /** O que trava o lançamento, escrito no próprio botão (D11) — nunca um botão sumido. */
    motivoDoLancamento() {
      if (this.isFishing) return '';
      if (!this.hasRod) return this.$t('fishing.buttons.needRod');
      if (!this.playerBaits.length) return this.$t('fishing.noBait');
      if (!this.selectedBaitId) return this.$t('fishing.buttons.selectBait');
      if (this.isResting) return this.$t('fishing.buttons.cantFish');
      return '';
    },
    canStartFishing() {
      return this.hasRod &&
             this.selectedBaitId &&
             this.playerBaits.find(b => b.id === this.selectedBaitId)?.quantity > 0 &&
             !this.isFishing &&
             !this.isResting;
    },
    hasRod() {
      return this.getEquippedRod !== null;
    },
    baitCount() {
      return this.inventory.baits.reduce((total, bait) => total + bait.quantity, 0);
    },
    rodDurabilityPercent() {
      if (!this.getEquippedRod?.durability?.current) return 0;
      return Math.round((this.getEquippedRod.durability.current / this.getEquippedRod.durability.maxDurability) * 100);
    },
    corDaDurabilidade() {
      if (this.rodDurabilityPercent > 50) return 'bom';
      if (this.rodDurabilityPercent > 20) return 'ouro';
      return 'ruim';
    },
    showHandFishing() {
      return !this.hasRod && this.inventory.rods.length === 0;
    }
  },
  methods: {
    ...mapActions('fishing', ['fish']),
    ...mapActions('SoundController', ['playSound']),
    ...mapActions('inventory', ['autoEquipFirstItem']),

    async tryAutoEquipRod() {
      if (!this.getEquippedRod && this.inventory.rods.length > 0) {
        try {
          await this.$store.dispatch('player/equipRod', {
            rodId: this.inventory.rods[0].id
          });
          await this.$store.dispatch('player/loadPlayerState', this.$store.state.player.id);
        } catch (error) {
          console.error('Failed to auto-equip rod:', error);
        }
      }
    },

    getItemImage(name) {
      return getItemImage(name);
    },
    selectBait(bait) {
      if (!bait || bait.quantity <= 0) return;
      this.selectedBaitId = bait.id;
      this.$store.commit('player/SELECT_BAIT', bait);
    },
    async playFishingAmbientSound() {
      if (this.currentAmbientSound) {
        this.currentAmbientSound.pause();
        this.currentAmbientSound = null;
      }

      let soundType;
      const mapType = (this.currentMap || '').toLowerCase();

      if (mapType.includes('pier') || mapType.includes('praia')) {
        soundType = 'ambientSoundPierAndBeach';
      } else if (mapType.includes('mar')) {
        soundType = 'ambientOpenSea';
      } else {
        soundType = 'ambientSoundOthers';
      }

      this.currentAmbientSound = await this.playSound({
        category: 'fishing',
        type: soundType,
        returnAudio: true
      });
    },
    async startFishing() {
      // Verificar se tem isca selecionada
      if (!this.selectedBaitId || !this.playerBaits.find(b => b.id === this.selectedBaitId)) {
        this.showAlert = true;
        this.alertMessage = this.$t('fishing.selectBait');
        return;
      }

      // Auto-equip check before starting
      if (!this.getEquippedRod && this.inventory.rods.length > 0) {
        await this.tryAutoEquipRod();
      }

      if (!this.canStartFishing) {
        this.showAlert = true;
        this.alertMessage = !this.hasRod
          ? this.$t('fishing.needRod')
          : this.$t('fishing.selectBait');
        return;
      }

      // Verificar durabilidade da vara antes de pescar
      if (this.getEquippedRod && this.rodDurabilityPercent <= 0) {
        await this.autoEquipNextRod();
        if (!this.getEquippedRod) return;
      }

      const canFish = await this.canFish;
      if (!canFish) {
        this.showAlert = true;
        this.alertMessage = this.$t('fishing.cantFishHere');
        return;
      }

      this.resetStates();
      this.isFishing = true;
      this.loadingProgress = 0;

      try {
        // Play ambient sound when starting to fish
        await this.playFishingAmbientSound();

        const reelSpeed = this.$store.state.player.stats.reelSpeed || 1;
        const baseWaitTime = 1000; // Base time in milliseconds
        const adjustedWaitTime = baseWaitTime / reelSpeed;

        const updateProgress = async (progress, message) => {
          if (this._isUnmounted) return; // Check if component is unmounted
          this.loadingProgress = progress;
          this.fishingState = message;
          await new Promise(resolve => setTimeout(resolve, 800 / reelSpeed));
        };

        const messages = [
          { progress: 15, message: this.$t('fishing.fishingMessages.preparing') },
          { progress: 25, message: this.$t('fishing.fishingMessages.casting') },
          { progress: 40, message: this.$t('fishing.fishingMessages.waiting') },
          { progress: 50, message: this.$t('fishing.fishingMessages.looking') },
          { progress: 60, message: this.$t('fishing.fishingMessages.movement') },
          { progress: 75, message: this.$t('fishing.fishingMessages.holding') },
          { progress: 85, message: this.$t('fishing.fishingMessages.pulling') },
        ];

        // Executa cada mensagem na sequência com tempo ajustado pelo reelSpeed
        for (const msg of messages) {
          await updateProgress(msg.progress, msg.message);
          await new Promise(resolve => setTimeout(resolve, adjustedWaitTime));
        }

        const result = await this.fish();
        this.aplicarResultado(result);

        // Parar o som ambiente antes de tocar o som de resultado
        if (this.currentAmbientSound) {
          this.currentAmbientSound.pause();
          this.currentAmbientSound = null;
        }

        // Play appropriate sound based on result
        if (result.success) {
          await this.playSound({ category: 'fishing', type: 'catch' });
          this.$store.commit('player/DECREMENT_BAIT_QUANTITY', this.selectedBaitId);
        } else {
          await this.playSound({ category: 'fishing', type: 'fail' });
        }

        // Tempo final de espera também ajustado pelo reelSpeed
        setTimeout(async () => {
          if (result.success) {
            this.caughtFish = result.fish;
            // O primeiro peixe do dia: o servidor manda o bônus creditado
            // (`firstFishBonus`), e é ele que decide — não a flag local.
            this.firstFishBonus = result.firstFishBonus || 0;
            if (this.firstFishBonus > 0 || !this.$store.state.player.firstFishOfDay) {
              this.showFirstFishCelebration = true;
            }

            // Update mission progress when a fish is caught
            try {
              const amountFishResult = await this.$store.dispatch('dailyMissions/updateMissionProgress', {
                activityType: 'amountCatchFish',
                amount: 1
              });

              let specificFishResult = null;
              // If this is a specific fish, also update specific fish mission
              if (this.caughtFish && this.caughtFish.id) {
                specificFishResult = await this.$store.dispatch('dailyMissions/updateMissionProgress', {
                  activityType: 'specificFish',
                  amount: this.caughtFish.id
                });
              }

              // Only check and show rewards if there was a meaningful mission update
              const shouldShowMissionsModal = this.shouldShowMissionsModal(amountFishResult, specificFishResult);

              if (shouldShowMissionsModal) {
                await this.$store.dispatch('rewards/checkAndShowRewards', this.$store.state.player.id);
              }
            } catch (missionError) {
              console.error('Failed to update mission progress:', missionError);
            }

            this.isFishing = false;
            this.loadingProgress = 0;
            this.$store.dispatch('player/loadPlayerState', this.$store.state.player.id);
          } else {
            this.fishingResult = result;
          }
          this.isFishing = false;
          this.loadingProgress = 0;
          this.$store.dispatch('player/loadPlayerState', this.$store.state.player.id);
        }, 1200 / reelSpeed);

        // Após pescar, verificar se a vara quebrou
        if (this.getEquippedRod && this.rodDurabilityPercent <= 0) {
          await this.autoEquipNextRod();
        }

      } catch (error) {
        console.error('Fishing failed:', error);
        this.resetStates();
        await this.playSound({ category: 'fishing', type: 'fail' });
      }
    },

    hideFirstFishCelebration() {
      this.showFirstFishCelebration = false;
    },

    getFishingResultMessage(result) {
      return this.$t(`fishing.results.${result.status.toLowerCase()}`);
    },
    resetStates() {
      // Stop any playing ambient sound
      if (this.currentAmbientSound) {
        this.currentAmbientSound.pause();
        this.currentAmbientSound = null;
      }
      this.isFishing = false;
      this.loadingProgress = 0;
      this.fishingState = '';
      this.fishingResult = null;
      this.caughtFish = null;
    },
    resetCatch() {
      this.caughtFish = null;
      this.fishingResult = null;
    },
    goToShop() {
      this.$router.push('/shop');
    },
    goToInventory() {
      this.$router.push('/inventory');
    },
    getTranslatedBaitName(baitName) {
      const translation = baitTranslations[baitName]?.name?.[this.$i18n.locale];
      return translation || baitName;
    },
    async autoEquipNextRod() {
      if (this.autoEquipInProgress) return;
      this.autoEquipInProgress = true;

      try {
        // `rod.equipped` vem do baú do V2. Comparar `rod.id` com o id da vara
        // equipada não funcionava: um é o id da instância, o outro o do catálogo.
        const availableRods = this.inventory.rods.filter(rod =>
          !rod.equipped &&
          (rod.durability?.current ?? 0) > 0
        );

        if (availableRods.length > 0) {
          await this.$store.dispatch('player/equipRod', {
            rodId: availableRods[0].id
          });
          this.showAlert = true;
          this.alertMessage = this.$t('fishing.rodAutoEquipped', { name: availableRods[0].name });
        } else {
          this.showAlert = true;
          this.alertMessage = this.$t('fishing.noRodsAvailable');
        }
      } catch (error) {
        console.error('Error auto-equipping rod:', error);
      } finally {
        this.autoEquipInProgress = false;
      }
    },
    selectBestBait() {
      if (!this.playerBaits.length) return;

      // Ordenar iscas por poder de pesca (do maior para o menor)
      const sortedBaits = [...this.playerBaits].sort((a, b) => b.fishingPower - a.fishingPower);

      if (sortedBaits.length > 0) {
        this.selectBait(sortedBaits[0]);
      }
    },
    checkAndUpdateBait() {
      // Se a isca atual acabou ou não existe mais
      if (!this.playerBaits.find(b => b.id === this.selectedBaitId)) {
        this.selectBestBait(); // Seleciona automaticamente a melhor isca disponível
      }
    },
    async startHandFishing() {
      if (this.isHandFishing) return;

      this.isHandFishing = true;
      this.resetStates();
      this.isFishing = true;
      this.loadingProgress = 0;

      try {
        await this.playFishingAmbientSound();
        const reelSpeed = 1; // Hand fishing is always normal speed
        const baseWaitTime = 1000;
        const adjustedWaitTime = baseWaitTime / reelSpeed;

        const updateProgress = async (progress, message) => {
          if (this._isUnmounted) return;
          this.loadingProgress = progress;
          this.fishingState = message;
          await new Promise(resolve => setTimeout(resolve, 800 / reelSpeed));
        };

        const messages = [
          { progress: 20, message: this.$t('fishing.handFishing.searching') },
          { progress: 40, message: this.$t('fishing.handFishing.waiting') },
          { progress: 60, message: this.$t('fishing.handFishing.spotted') },
          { progress: 80, message: this.$t('fishing.handFishing.reaching') },
        ];

        for (const msg of messages) {
          await updateProgress(msg.progress, msg.message);
          await new Promise(resolve => setTimeout(resolve, adjustedWaitTime));
        }

        const result = await this.$store.dispatch('fishing/fish', { isHandFishing: true });
        this.aplicarResultado(result);

        if (this.currentAmbientSound) {
          this.currentAmbientSound.pause();
          this.currentAmbientSound = null;
        }

        if (result.success) {
          await this.playSound({ category: 'fishing', type: 'catch' });
        } else {
          await this.playSound({ category: 'fishing', type: 'fail' });
        }

        setTimeout(() => {
          if (result.success) {
            this.caughtFish = result.fish;

            // Update mission progress for hand fishing too
            try {
              this.$store.dispatch('dailyMissions/updateMissionProgress', {
                activityType: 'amountCatchFish',
                amount: 1
              });

              if (this.caughtFish && this.caughtFish.id) {
                this.$store.dispatch('dailyMissions/updateMissionProgress', {
                  activityType: 'specificFish',
                  amount: this.caughtFish.id
                });
              }
            } catch (missionError) {
              console.error('Failed to update mission progress:', missionError);
            }
          } else {
            this.fishingResult = result;
          }
          this.isFishing = false;
          this.isHandFishing = false;
          this.loadingProgress = 0;
          this.$store.dispatch('player/loadPlayerState', this.$store.state.player.id);
        }, 1200 / reelSpeed); // Adjust final timeout with reelSpeed

      } catch (error) {
        console.error('Hand fishing failed:', error);
        this.resetStates();
        this.isHandFishing = false;
        await this.playSound({ category: 'fishing', type: 'fail' });
      }
    },

    /**
     * XP, nível e energia que o lance mudou, direto da resposta. A releitura
     * completa (`loadPlayerState`) continua depois, mas é seis GETs juntos e
     * falha calada — foi o que deixava a barra de XP parada (23/09/2026).
     */
    aplicarResultado(result) {
      if (!result || result.status === 'ERROR') return;
      this.$store.commit('player/APLICAR_CAPTURA', {
        experience: result.playerExperience,
        maxExperience: result.maxExperience,
        level: result.playerLevel,
        staminaRemaining: result.staminaRemaining,
      });
    },

    shouldShowMissionsModal(amountFishResult, specificFishResult) {
      // First check if either result indicates a newly completed mission
      if (
        (amountFishResult && amountFishResult.newlyCompletedMissions && amountFishResult.newlyCompletedMissions.length > 0) ||
        (specificFishResult && specificFishResult.newlyCompletedMissions && specificFishResult.newlyCompletedMissions.length > 0)
      ) {
        return true; // Show modal if any mission was newly completed
      }

      // Check if there was a significant progress update (but not completion) for amount missions
      if (amountFishResult && amountFishResult.updated && !amountFishResult.allComplete) {
        const missions = this.$store.getters['dailyMissions/missionsWithProgress'];

        const fishingMissions = missions.filter(mission =>
          mission.type === 'amountCatchFish' &&
          !mission.isComplete &&
          mission.progress > 0
        );

        return fishingMissions.length > 0;
      }

      return false;
    }
  },
  async created() {
    try {
      const isHouse = await this.$store.dispatch('player/getIsHouse', this.$store.state.player.id);
      this.isResting = isHouse.data.isHouse;
    } catch (error) {
      console.error('Failed to get house status:', error);
      this.isResting = false;
    }

    // Auto-equip rod on component creation if none is equipped
    if (!this.getEquippedRod && this.inventory.rods.length > 0) {
      await this.autoEquipNextRod();
    }

    // Add auto-equip check on component creation
    await this.tryAutoEquipRod();

    // Selecionar a melhor isca automaticamente
    this.selectBestBait();
  },
  beforeUnmount() {
    this.resetStates();
  },
  watch: {
    // A vara que quebra some do baú no servidor (13/09/2026): o slot fica vazio e
    // a durabilidade chega aqui como `undefined`, que o `<= 0` sozinho deixava passar.
    'getEquippedRod.durability.current': {
      handler(newValue) {
        if ((newValue ?? 0) <= 0) {
          this.tryAutoEquipRod();
        }
      },
      immediate: true
    },
    // Adicionar watcher para inventário de iscas
    'inventory.baits': {
      handler() {
        this.checkAndUpdateBait();
      },
      deep: true
    },

    // Adicionar watcher para selectedBaitId
    selectedBaitId: {
      handler(newId) {
        if (newId && !this.playerBaits.find(b => b.id === newId)) {
          this.selectBestBait();
        }
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
/**
 * A pescaria sem moldura (D3): a coluna do pescador fica encostada à esquerda em papel, o resto da
 * tela é ÁGUA — a cena do mapa é a protagonista. O boneco de CSS (fisherman-in-a-bowl) saiu: era
 * arte desenhada à mão em vetor arredondado, a maior distância possível do pixel do jogo, e a
 * regra do redesenho é recortar do pack ou não ter.
 */
.pescaria-acao {
  min-height: 100vh;
  padding: calc(72px + env(safe-area-inset-top, 0px)) var(--e-3) calc(150px + env(safe-area-inset-bottom, 0px));
  display: flex;
  gap: var(--e-4);
  align-items: flex-start;
  max-width: 1200px;
  margin: 0 auto;
}

.pescaria-acao__coluna {
  width: min(340px, 100%);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
}

/* Desktop lê a um braço de distância: a coluna e o botão crescem junto com a escala (veto do
 * autor, 03/09: "pequeno demais para pc"). */
@media (min-width: 1280px) {
  .pescaria-acao__coluna {
    width: 440px;
  }

  .pescaria-acao__bilhete {
    padding: var(--e-4);
  }

  .pescaria-acao__lancar {
    width: min(520px, calc(100vw - 32px));
  }
}

/* O palco: a espera e a falha aparecem no meio da água, não num painel. */
.pescaria-acao__palco {
  flex: 1;
  align-self: center;
  display: flex;
  justify-content: center;
}

/* O bilhete: papel com a moldura do pack — o mesmo material do mural do Cais. */
.pescaria-acao__bilhete {
  border: var(--moldura-borda) solid transparent;
  border-image: url('/ui/frame-panel.png') var(--moldura-fatia) / var(--moldura-borda) repeat;
  background: var(--papel);
  background-clip: padding-box;
  image-rendering: pixelated;
  padding: var(--e-3);
  color: var(--tinta);
  box-shadow: 0 4px 0 rgba(0, 0, 0, 0.3);
}

.pescaria-acao__atributos {
  display: flex;
  justify-content: space-between;
  gap: var(--e-2);
}

.pescaria-acao__atributo {
  display: flex;
  align-items: center;
  gap: var(--e-1);
  font-size: var(--f-2);
  color: var(--tinta);
}

.pescaria-acao__vara {
  display: flex;
  align-items: center;
  gap: var(--e-2);
}

.pescaria-acao__nome {
  margin: 0 0 var(--e-1);
  font-family: var(--fonte-display);
  font-size: var(--f-2);
}

.pescaria-acao__rotulo {
  margin: 0 0 var(--e-2);
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
  border-bottom: 2px dashed var(--papel-sombra);
  padding-bottom: var(--e-1);
}

.pescaria-acao__iscas {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--e-2);
  padding: 3px; /* o aro de raridade/seleção sai 2-3px para fora do encaixe */
}

.pescaria-acao__isca-nota {
  margin: var(--e-2) 0 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.pescaria-acao__aviso p {
  margin: 0 0 var(--e-2);
  font-size: var(--f-2);
}

.pescaria-acao__miudo {
  margin: 0 0 var(--e-1);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.pescaria-acao__regras {
  margin: 0 0 var(--e-3);
  padding-left: var(--e-4);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.pescaria-acao__espera {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--e-3);
}

.pescaria-acao__falha {
  min-width: min(360px, 80vw);
  text-align: center;
}

/* A fala do pescador: um bilhete curto embaixo do barco, trocando a cada instante da espera. */
.pescaria-acao__estado {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  text-align: center;
  min-width: min(320px, 80vw);
}

.pescaria-acao__falha p {
  margin: 0 0 var(--e-2);
  font-size: var(--f-2);
}

/* A ação principal na base, como no Cais: acima da barra, largura de polegar. */
.pescaria-acao__lancar {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: calc(var(--altura-barra, 72px) + var(--e-3) + env(safe-area-inset-bottom, 0px));
  width: min(420px, calc(100vw - 32px));
  z-index: 10;
  background: none;
  padding: 0;
}

@media (max-width: 899px) {
  .pescaria-acao {
    flex-direction: column;
    align-items: stretch;
    gap: var(--e-3);
  }

  .pescaria-acao__coluna {
    width: 100%;
  }

  .pescaria-acao__palco {
    align-self: stretch;
  }
}
</style>
